import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Storefront, MagnifyingGlass, CircleNotch, ArrowsClockwise, Eye, EyeSlash,
  Package, UploadSimple, CaretLeft, CaretRight, ArrowSquareOut, X, Check, FolderSimple, PencilSimple, WhatsappLogo, ChartBar, VideoCamera,
} from '@phosphor-icons/react';
import { TiendaReportesTab } from './TiendaReportesTab';
import TiendaPortadaAdmin from './TiendaPortadaAdmin';

const EMPRESA = 'complementos';
const fmtQ = (n) => n == null ? '—' : `Q ${Number(n).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FILTROS = [
  { key: '', label: 'Todos' },
  { key: 'modificados', label: 'Modificados' },
  { key: 'ocultos', label: 'Ocultos' },
  { key: 'sin_imagen', label: 'Sin imagen' },
  { key: 'sin_precio', label: 'Sin precio' },
  { key: 'bajo_costo', label: '⚠ Precio bajo costo' },
];

const PrecioEditor = ({ p, onSave }) => {
  const [val, setVal] = useState(p.precio_override ?? '');
  useEffect(() => setVal(p.precio_override ?? ''), [p.precio_override]);
  const save = () => {
    const num = parseFloat(val);
    if (val === '' || isNaN(num)) return;
    onSave(p.sku, { precio_override: num });
  };
  return (
    <div className="flex items-center gap-1.5">
      <input
        data-testid={`precio-input-${p.sku}`}
        type="number" min="0" step="0.01"
        placeholder={String(p.precio_erp || 0)}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
        className="w-24 h-9 px-2 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
      />
      {val !== '' && parseFloat(val) !== p.precio_override && (
        <button data-testid={`precio-save-${p.sku}`} onClick={save} className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" title="Guardar precio">
          <Check size={14} weight="bold" />
        </button>
      )}
      {p.precio_override != null && (
        <button data-testid={`precio-clear-${p.sku}`} onClick={() => { setVal(''); onSave(p.sku, { clear_precio: true }); }}
          className="p-1.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 hover:text-red-600" title="Restaurar precio ERP">
          <X size={14} weight="bold" />
        </button>
      )}
    </div>
  );
};

const FamiliaSelect = ({ p, familias, onSave }) => (
  <div className="flex items-center gap-1">
    <select
      data-testid={`familia-select-${p.sku}`}
      value={p.categoria}
      onChange={(e) => onSave(p.sku, { categoria_override: e.target.value })}
      className="h-9 max-w-[160px] px-1.5 border border-slate-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-800"
      title="Mover a otra familia"
    >
      {!familias.includes(p.categoria) && <option value={p.categoria}>{p.categoria}</option>}
      {familias.map((f) => <option key={f} value={f}>{f}</option>)}
    </select>
    {p.categoria_override && (
      <button data-testid={`familia-clear-${p.sku}`} onClick={() => onSave(p.sku, { clear_categoria: true })}
        className="p-1.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 hover:text-red-600" title={`Restaurar familia ERP (${p.categoria_erp})`}>
        <X size={14} weight="bold" />
      </button>
    )}
  </div>
);

const NombreEditor = ({ p, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const start = () => { setVal(p.descripcion); setEditing(true); };
  const save = () => {
    if (val.trim() && val.trim() !== p.descripcion) onSave(p.sku, { nombre_override: val.trim() });
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          data-testid={`nombre-input-${p.sku}`}
          value={val} autoFocus
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          className="flex-1 h-8 px-2 border border-blue-400 rounded text-sm bg-white dark:bg-slate-800"
        />
        <button data-testid={`nombre-save-${p.sku}`} onClick={save} className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"><Check size={13} weight="bold" /></button>
        <button onClick={() => setEditing(false)} className="p-1.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500"><X size={13} weight="bold" /></button>
      </div>
    );
  }
  return (
    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate flex items-center gap-1.5" title={p.descripcion}>
      <span className="truncate">{p.descripcion}</span>
      <button data-testid={`nombre-edit-${p.sku}`} onClick={start} className="shrink-0 p-1 text-slate-400 hover:text-blue-600 transition-colors" title="Editar nombre (solo tienda)">
        <PencilSimple size={14} />
      </button>
      {p.nombre_override && (
        <button data-testid={`nombre-clear-${p.sku}`} onClick={() => onSave(p.sku, { clear_nombre: true })}
          className="shrink-0 p-1 text-slate-400 hover:text-red-600 transition-colors" title={`Restaurar nombre ERP: ${p.descripcion_erp}`}>
          <X size={14} />
        </button>
      )}
    </p>
  );
};

const ProductRow = ({ p, familias, onSave, onUploadImage, uploading, onUploadVideo, uploadingVideo }) => {
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  return (
    <div data-testid={`admin-producto-${p.sku}`} className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg ${p.hidden ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-14 h-14 shrink-0 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded flex items-center justify-center overflow-hidden">
          {p.imagen_url ? (
            <img src={p.imagen_url} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <Package size={22} className="text-slate-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <NombreEditor p={p} onSave={onSave} />
          <p className="text-xs text-slate-500">
            SKU: {p.sku} · Stock: {p.existencia}
            {p.nombre_override && <span className="ml-1 text-blue-600 font-medium">· Nombre editado</span>}
            {p.categoria_override && <span className="ml-1 text-blue-600 font-medium">· Movido de {p.categoria_erp}</span>}
            {p.familia_oculta && <span className="ml-1 text-amber-600 font-medium">· Familia oculta</span>}
            {p.imagen_override && <span className="ml-1 text-blue-600 font-medium">· Imagen propia</span>}
            {p.video_override && <span className="ml-1 text-purple-600 font-medium">· 🎬 Video</span>}
          </p>
          <p className="text-xs text-slate-500">
            Precio ERP: <span className="font-semibold">{fmtQ(p.precio_erp)}</span>
            {p.ultcosto > 0 && (
              <span className={p.precio < p.ultcosto ? 'ml-2 text-red-600 font-semibold' : 'ml-2'}>
                Costo: {fmtQ(p.ultcosto)}{p.precio < p.ultcosto && ' ⚠ vende bajo costo'}
              </span>
            )}
            {p.precio_override != null && (
              <span className="ml-2 text-emerald-600 font-semibold">→ En tienda: {fmtQ(p.precio_override)}</span>
            )}
            {p.precio <= 0 && !p.hidden && <span className="ml-2 text-amber-600 font-semibold">⚠ Sin precio (no se muestra)</span>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <FamiliaSelect p={p} familias={familias} onSave={onSave} />
        <PrecioEditor p={p} onSave={onSave} />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUploadImage(p.sku, f);
          e.target.value = '';
        }} />
        <button
          data-testid={`upload-image-${p.sku}`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading === p.sku}
          className="h-9 px-3 flex items-center gap-1.5 text-xs font-semibold border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {uploading === p.sku ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} />}
          Imagen
        </button>
        {p.imagen_override && (
          <button data-testid={`clear-image-${p.sku}`} onClick={() => onSave(p.sku, { clear_imagen: true })}
            className="h-9 px-2 text-xs text-slate-500 hover:text-red-600 border border-slate-300 dark:border-slate-600 rounded" title="Quitar imagen personalizada">
            <X size={14} />
          </button>
        )}
        <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUploadVideo(p.sku, f);
          e.target.value = '';
        }} />
        <button
          data-testid={`upload-video-${p.sku}`}
          onClick={() => videoRef.current?.click()}
          disabled={uploadingVideo === p.sku}
          title="Subir video del producto (máx 60MB)"
          className={`h-9 px-3 flex items-center gap-1.5 text-xs font-semibold border rounded transition-colors disabled:opacity-50 ${p.video_override ? 'border-purple-400 text-purple-600 dark:text-purple-400' : 'border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
        >
          {uploadingVideo === p.sku ? <CircleNotch size={14} className="animate-spin" /> : <VideoCamera size={14} />}
          Video
        </button>
        {p.video_override && (
          <button data-testid={`clear-video-${p.sku}`} onClick={() => onSave(p.sku, { clear_video: true })}
            className="h-9 px-2 text-xs text-slate-500 hover:text-red-600 border border-slate-300 dark:border-slate-600 rounded" title="Quitar video">
            <X size={14} />
          </button>
        )}
        <button
          data-testid={`toggle-hidden-${p.sku}`}
          onClick={() => onSave(p.sku, { hidden: !p.hidden })}
          className={`h-9 px-3 flex items-center gap-1.5 text-xs font-semibold rounded transition-colors ${
            p.hidden ? 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300' : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
          }`}
        >
          {p.hidden ? <><EyeSlash size={14} /> Oculto</> : <><Eye size={14} /> Visible</>}
        </button>
      </div>
    </div>
  );
};

const FamiliaRow = ({ f, onSave, onVerProductos }) => {
  const [nombre, setNombre] = useState(f.nombre_override || '');
  useEffect(() => setNombre(f.nombre_override || ''), [f.nombre_override]);
  const rename = () => {
    if (!nombre.trim() || nombre.trim().toUpperCase() === (f.nombre_override || '')) return;
    onSave(f.original, { nombre_override: nombre.trim() });
  };
  return (
    <div data-testid={`familia-row-${f.original}`} className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg ${f.hidden ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <button data-testid={`familia-ver-productos-${f.original}`} onClick={() => onVerProductos(f.original)}
          className="text-left w-full group" title="Ver los productos de esta familia">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase group-hover:text-blue-600 transition-colors">
            {f.nombre_override ? (
              <>{f.nombre_override} <span className="text-xs font-normal text-blue-600">(original: {f.original})</span></>
            ) : f.original}
          </p>
          <p className="text-xs text-slate-500">{f.productos} producto{f.productos !== 1 ? 's' : ''} con existencia · <span className="text-blue-600 font-semibold group-hover:underline">ver productos →</span></p>
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid={`familia-nombre-input-${f.original}`}
          placeholder="Nuevo nombre..."
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && rename()}
          className="w-48 h-9 px-2 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800"
        />
        {nombre.trim() && nombre.trim().toUpperCase() !== (f.nombre_override || '') && (
          <button data-testid={`familia-rename-save-${f.original}`} onClick={rename} className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" title="Guardar nombre">
            <Check size={14} weight="bold" />
          </button>
        )}
        {f.nombre_override && (
          <button data-testid={`familia-nombre-clear-${f.original}`} onClick={() => { setNombre(''); onSave(f.original, { clear_nombre: true }); }}
            className="p-1.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 hover:text-red-600" title="Restaurar nombre original">
            <X size={14} weight="bold" />
          </button>
        )}
        <button
          data-testid={`familia-toggle-hidden-${f.original}`}
          onClick={() => onSave(f.original, { hidden: !f.hidden })}
          className={`h-9 px-3 flex items-center gap-1.5 text-xs font-semibold rounded transition-colors ${
            f.hidden ? 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300' : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
          }`}
        >
          {f.hidden ? <><EyeSlash size={14} /> Oculta</> : <><Eye size={14} /> Visible</>}
        </button>
      </div>
    </div>
  );
};

const FamiliasTab = ({ onVerProductos }) => {
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFamilias = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tienda/${EMPRESA}/admin/familias`);
      setFamilias(data.familias);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando familias');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFamilias(); }, [fetchFamilias]);

  const saveFamilia = async (original, payload) => {
    try {
      const { data } = await api.put(`/tienda/${EMPRESA}/admin/familia/${encodeURIComponent(original)}`, payload);
      const fam = data.familia || {};
      setFamilias((prev) => prev.map((f) => f.original !== original ? f : {
        ...f,
        hidden: !!fam.hidden,
        nombre_override: fam.nombre_override || null,
        nombre: fam.nombre_override || f.original,
      }));
      toast.success('Familia actualizada — la tienda se actualiza al instante');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando familia');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={28} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-2" data-testid="familias-tab">
      <p className="text-xs text-slate-500">Haz clic en el nombre de una familia para ver y editar sus productos (ocultar, precio, imagen, video). Renombra u oculta familias aquí — ocultar una familia retira todos sus productos de la tienda.</p>
      {familias.map((f) => <FamiliaRow key={f.original} f={f} onSave={saveFamilia} onVerProductos={onVerProductos} />)}
    </div>
  );
};

export default function TiendaAdmin() {
  const [tab, setTab] = useState('productos');
  const [productos, setProductos] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [total, setTotal] = useState(0);
  const [visibles, setVisibles] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [filtro, setFiltro] = useState('');
  const [catFiltro, setCatFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testingWa, setTestingWa] = useState(false);
  const [waResults, setWaResults] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(null);
  const [bulkPct, setBulkPct] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchProductos = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tienda/${EMPRESA}/admin/productos`, {
        params: { q: q || undefined, filtro: filtro || undefined, categoria: catFiltro || undefined, page: pg, per_page: 30 },
      });
      setProductos(data.data);
      setFamilias(data.familias || []);
      setTotal(data.total);
      setVisibles(data.visibles);
      setPages(data.pages);
      setPage(pg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando productos');
    } finally {
      setLoading(false);
    }
  }, [q, filtro, catFiltro]);

  useEffect(() => { fetchProductos(1); }, [fetchProductos]);

  const onSearchChange = (v) => {
    setQInput(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(v), 400);
  };

  const saveOverride = async (sku, payload) => {
    try {
      const { data } = await api.put(`/tienda/${EMPRESA}/admin/override/${encodeURIComponent(sku)}`, payload);
      const ov = data.override || {};
      setProductos((prev) => prev.map((p) => {
        if (p.sku !== sku) return p;
        const precio_override = ov.precio_override ?? null;
        const imagen_override = ov.imagen_override ?? null;
        const categoria_override = ov.categoria_override ?? null;
        const nombre_override = ov.nombre_override ?? null;
        return {
          ...p,
          hidden: !!ov.hidden,
          precio_override,
          imagen_override,
          categoria_override,
          nombre_override,
          descripcion: nombre_override || p.descripcion_erp,
          categoria: categoria_override || p.categoria_erp,
          precio: precio_override != null && precio_override > 0 ? precio_override : p.precio_erp,
          imagen_url: imagen_override || p.imagen_erp,
        };
      }));
      toast.success('Guardado — la tienda se actualiza al instante');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    }
  };

  const uploadImage = async (sku, file) => {
    setUploading(sku);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await saveOverride(sku, { imagen_override: data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error subiendo imagen');
    } finally {
      setUploading(null);
    }
  };

  const uploadVideo = async (sku, file) => {
    if (file.size > 60 * 1024 * 1024) {
      toast.error('Video demasiado grande (máx 60MB). Recórtalo o comprímelo.');
      return;
    }
    setUploadingVideo(sku);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/tienda/${EMPRESA}/admin/upload-video`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      await saveOverride(sku, { video_override: data.url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error subiendo video');
    } finally {
      setUploadingVideo(null);
    }
  };

  const bulkFamilia = async (accion) => {
    const msgs = {
      ocultar_sin_imagen: `¿Ocultar de la tienda TODOS los productos SIN imagen de la familia "${catFiltro}"?`,
      ajustar_precio: `¿Ajustar el precio de TODA la familia "${catFiltro}" en ${bulkPct}%? (se guarda como precio personalizado)`,
      restaurar_precios: `¿Restaurar los precios originales del ERP para toda la familia "${catFiltro}"? (quita los precios personalizados)`,
    };
    if (!window.confirm(msgs[accion])) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post(`/tienda/${EMPRESA}/admin/familia/${encodeURIComponent(catFiltro)}/bulk`, {
        accion, pct: accion === 'ajustar_precio' ? parseFloat(bulkPct) : undefined,
      });
      toast.success(`Listo: ${data.afectados} producto(s) afectado(s)`);
      setBulkPct('');
      fetchProductos(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en acción masiva');
    } finally {
      setBulkLoading(false);
    }
  };

  const refreshCatalog = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/tienda/${EMPRESA}/admin/refresh`);
      toast.success(`Catálogo actualizado desde HarpERP: ${data.productos} productos`);
      fetchProductos(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error actualizando catálogo');
    } finally {
      setRefreshing(false);
    }
  };

  const testWhatsapp = async () => {
    setTestingWa(true);
    setWaResults(null);
    try {
      const { data } = await api.post(`/tienda/${EMPRESA}/admin/test-whatsapp`, {});
      setWaResults(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en diagnóstico');
    } finally {
      setTestingWa(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="tienda-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <Storefront size={26} className="text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Tienda en Línea</h1>
            <p className="text-xs text-slate-500">{visibles} visibles de {total} productos con existencia</p>
          </div>
        </div>
        <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
          <button
            data-testid="test-whatsapp-button"
            onClick={testWhatsapp}
            disabled={testingWa}
            className="h-10 px-3 flex items-center gap-1.5 text-sm font-semibold border border-emerald-400 text-emerald-700 dark:text-emerald-400 rounded-md hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors disabled:opacity-50"
          >
            {testingWa ? <CircleNotch size={16} className="animate-spin" /> : <WhatsappLogo size={16} weight="fill" />} Probar WhatsApp
          </button>
          <a
            data-testid="ver-tienda-link"
            href={`/tienda/${EMPRESA}?from=crm`}
            target="_blank"
            rel="noreferrer"
            className="h-10 px-3 flex items-center gap-1.5 text-sm font-semibold border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowSquareOut size={16} /> Ver tienda
          </a>
          <button
            data-testid="refresh-catalog-button"
            onClick={refreshCatalog}
            disabled={refreshing}
            className="h-10 px-3 flex items-center gap-1.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
          >
            <ArrowsClockwise size={16} className={refreshing ? 'animate-spin' : ''} /> Sincronizar ERP
          </button>
        </div>
      </div>

      {/* Resultados diagnóstico WhatsApp */}
      {waResults && (
        <div data-testid="wa-results-card" className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
              Diagnóstico WhatsApp → {waResults.destino} · {waResults.lineas_con_token} línea(s) con token de {waResults.negocios_totales} negocios
            </p>
            <button onClick={() => setWaResults(null)} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
          </div>
          {waResults.lineas_con_token === 0 && (
            <p className="text-xs text-red-600 font-semibold">⚠ Ningún negocio tiene token de WhatsApp configurado. Ve a Configuración → Token global de WhatsApp → Aplicar a todos los negocios.</p>
          )}
          {waResults.resultados.map((r, i) => (
            <div key={i} className={`text-xs p-2 rounded border ${r.ok ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300' : 'border-red-200 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'}`}>
              <span className="font-bold">{r.ok ? '✅' : '❌'} {r.linea}</span>
              {r.codigo ? ` · Error ${r.codigo}` : ''} · {r.detalle}
              {r.sugerencia && <div className="mt-0.5 font-semibold">→ {r.sugerencia}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        <button data-testid="tab-productos" onClick={() => setTab('productos')}
          className={`h-10 px-4 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === 'productos' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
          <Package size={16} /> Productos
        </button>
        <button data-testid="tab-familias" onClick={() => setTab('familias')}
          className={`h-10 px-4 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === 'familias' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
          <FolderSimple size={16} /> Familias
        </button>
        <button data-testid="tab-portada" onClick={() => setTab('portada')}
          className={`h-10 px-4 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === 'portada' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
          <Storefront size={16} /> Portada
        </button>
        <button data-testid="tab-reportes" onClick={() => setTab('reportes')}
          className={`h-10 px-4 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === 'reportes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}>
          <ChartBar size={16} /> Reportes
        </button>
      </div>

      {tab === 'portada' ? (
        <TiendaPortadaAdmin />
      ) : tab === 'reportes' ? (
        <TiendaReportesTab empresa={EMPRESA} />
      ) : tab === 'familias' ? (
        <FamiliasTab onVerProductos={(orig) => { setCatFiltro(orig); setTab('productos'); }} />
      ) : (
        <>
          {catFiltro && (
            <div className="space-y-2" data-testid="familia-filter-chip">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-bold rounded-full bg-blue-600 text-white">
                  <FolderSimple size={13} /> Familia: {catFiltro}
                  <button data-testid="familia-filter-clear" onClick={() => setCatFiltro('')} className="ml-1 hover:text-blue-200" title="Quitar filtro de familia">
                    <X size={13} weight="bold" />
                  </button>
                </span>
                <span className="text-xs text-slate-500">Mostrando solo los productos de esta familia</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap p-2.5 border border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 rounded-md" data-testid="familia-bulk-bar">
                <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase">Acciones masivas:</span>
                <button data-testid="bulk-ocultar-sin-imagen" onClick={() => bulkFamilia('ocultar_sin_imagen')} disabled={bulkLoading}
                  className="h-8 px-3 text-xs font-bold rounded-md border border-slate-400 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1 disabled:opacity-50">
                  <EyeSlash size={13} /> Ocultar sin imagen
                </button>
                <div className="flex items-center gap-1">
                  <input data-testid="bulk-pct-input" type="number" placeholder="%" value={bulkPct} onChange={(e) => setBulkPct(e.target.value)}
                    className="w-20 h-8 px-2 text-xs border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800" />
                  <button data-testid="bulk-ajustar-precio" onClick={() => bulkFamilia('ajustar_precio')} disabled={bulkLoading || !bulkPct}
                    className="h-8 px-3 text-xs font-bold rounded-md border border-blue-400 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50">
                    Ajustar precios ±%
                  </button>
                </div>
                <button data-testid="bulk-restaurar-precios" onClick={() => bulkFamilia('restaurar_precios')} disabled={bulkLoading}
                  className="h-8 px-3 text-xs font-bold rounded-md border border-slate-300 dark:border-slate-600 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
                  Restaurar precios ERP
                </button>
                {bulkLoading && <CircleNotch size={16} className="animate-spin text-amber-600" />}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="admin-search-input"
                placeholder="Buscar por nombre o SKU..."
                value={qInput}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full h-10 pl-9 pr-3 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-800"
              />
            </div>
            <div className="flex gap-1.5 overflow-x-auto">
              {FILTROS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`filtro-${f.key || 'todos'}`}
                  onClick={() => setFiltro(f.key)}
                  className={`h-10 px-3 text-xs font-semibold rounded-md whitespace-nowrap transition-colors ${
                    filtro === f.key ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><CircleNotch size={28} className="animate-spin text-blue-600" /></div>
          ) : productos.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <Package size={36} className="mx-auto mb-3" />
              <p className="text-sm">No hay productos con este filtro.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {productos.map((p) => (
                <ProductRow key={p.sku} p={p} familias={familias} onSave={saveOverride} onUploadImage={uploadImage} uploading={uploading} onUploadVideo={uploadVideo} uploadingVideo={uploadingVideo} />
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button data-testid="admin-prev-page" disabled={page <= 1} onClick={() => fetchProductos(page - 1)}
                className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-40"><CaretLeft size={14} /></button>
              <span className="text-sm text-slate-500">Página {page} de {pages}</span>
              <button data-testid="admin-next-page" disabled={page >= pages} onClick={() => fetchProductos(page + 1)}
                className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded disabled:opacity-40"><CaretRight size={14} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
